import type { Contact, ContactAssignedTag } from '@/types/contact';

function asOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function readObject(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function normalizeContactAssignedTags(
  rawTags: unknown,
  rawTagIds?: unknown,
): ContactAssignedTag[] {
  const byId = new Map<string, ContactAssignedTag>();

  function addTag(tag: ContactAssignedTag) {
    if (!tag.tagId || byId.has(tag.tagId)) return;
    byId.set(tag.tagId, tag);
  }

  function addRaw(value: unknown) {
    const directId = asOptionalString(value);
    if (directId) {
      addTag({ tagId: directId, id: directId });
      return;
    }

    const record = readObject(value);
    if (!record) return;

    const nestedTag = readObject(record.tag);
    const tagId =
      asOptionalString(record.tagId) ??
      asOptionalString(nestedTag?.id) ??
      asOptionalString(record.id) ??
      asOptionalString(record._id);

    if (!tagId) return;

    addTag({
      tagId,
      id: tagId,
      name:
        asOptionalString(record.name) ??
        asOptionalString(record.tagName) ??
        asOptionalString(record.tag) ??
        asOptionalString(nestedTag?.name),
      color: asOptionalString(record.color) ?? asOptionalString(nestedTag?.color) ?? null,
    });
  }

  if (Array.isArray(rawTags)) {
    rawTags.forEach(addRaw);
  }
  if (Array.isArray(rawTagIds)) {
    rawTagIds.forEach(addRaw);
  }

  return Array.from(byId.values());
}

export function getContactTagIds(
  contact: Pick<Contact, 'tags' | 'tagIds'> | null | undefined,
): string[] {
  if (!contact) return [];
  return normalizeContactAssignedTags(contact.tags, contact.tagIds).map((tag) => tag.tagId);
}
